import BidAnalyticsIcon from "@/assets/images/bid-analytics-icon-dark-red.png";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useReadSpecsReagents,
  useUpdateSpecsReagents,
} from "@/libs/react-query/hooks/useSpecsQuery";
import { queryClient } from "@/libs/react-query/queryClient";
import { QUERY_KEYS } from "@/libs/react-query/queryKeys";
import { cn } from "@/libs/utils";
import { ReagentOwnType, SpecReadReagentsResponse } from "@/types/specs";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { useSpecAnalysisStore } from "../stores/useSpecAnalysisStore";
import { convertToFormValues, parsePrice, parseProjectType } from "../utils";
import { createChemicalSpecColumns } from "./columns/chemicalColumns";
import Card from "./common/Card";

const reagentsSchema = z.object({
  reagents: z.array(
    z.object({
      spec_id: z.string(),
      alt_items: z.array(
        z.object({
          alt_map_id: z.string(),
          selected: z.boolean(),
        })
      ),
    })
  ),
});
export type ReagentsSchema = z.infer<typeof reagentsSchema>;

export default function ChemicalSpecs() {
  const { projectId, projectType } = useParams();
  const { setBudgetPrice, setSumPrice, setMatchRate } = useSpecAnalysisStore();
  const [ownTypeFilter, setOwnTypeFilter] = useState<string>("all");
  const [manufacturerFilter, setManufacturerFilter] = useState<string[]>([]);

  // setter 함수들을 useCallback으로 안정화
  const onChangeOwnTypeFilter = useCallback((value: string) => {
    setOwnTypeFilter(value);
  }, []);

  const onChangeManufacturerFilter = useCallback((value: string[]) => {
    setManufacturerFilter(value);
  }, []);

  const { data, isLoading } = useReadSpecsReagents({
    project_type: parseProjectType(projectType),
    project_id: projectId ?? "",
  });
  const { mutate } = useUpdateSpecsReagents({
    project_type: parseProjectType(projectType),
    project_id: projectId ?? "",
  });
  const { budget_amount = 0, reagents = [] } = data || {};

  const initialValues = useMemo(() => {
    return convertToFormValues(data);
  }, [data]);

  const form = useForm<ReagentsSchema>({
    resolver: zodResolver(reagentsSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = form;

  const manufacturers = useMemo(() => {
    const uniqueManufacturers = Array.from(
      new Set(reagents.map((r) => r.manufacturer))
    );
    return uniqueManufacturers
      .filter((m): m is string => m !== undefined)
      .sort();
  }, [reagents]);

  const hasEmptyManufacturers = useMemo(() => {
    return reagents.some(
      (r) =>
        r.manufacturer === undefined ||
        r.manufacturer === null ||
        r.manufacturer === ""
    );
  }, [reagents]);

  // globalFilter 객체 메모화
  const globalFilterValue = useMemo(
    () => ({
      ownType: ownTypeFilter,
      manufacturers: manufacturerFilter,
    }),
    [ownTypeFilter, manufacturerFilter]
  );

  // globalFilterFn 메모화
  const globalFilterFn = useCallback(
    (row: any, _columnId: string, filterValue: any) => {
      const { ownType, manufacturers } = filterValue as {
        ownType: string;
        manufacturers: string[];
      };

      // 자사/타사 필터 (로직 수정)
      if (ownType === "in-house") {
        // "자사제품" 선택 시 자사제품만 표시
        if (row.original.own_type === ReagentOwnType.IN_HOUSE) {
          return true;
        } else {
          return false;
        }
      }
      if (ownType === "external") {
        // "타사제품" 선택 시 타사제품만 표시
        if (row.original.own_type !== ReagentOwnType.IN_HOUSE) {
          return true;
        } else {
          return false;
        }
      }

      // 제조사 필터 (빈 배열이면 통과)
      if (manufacturers.length > 0) {
        const rowManufacturer = row.original.manufacturer;
        const hasEmptyFilter = manufacturers.includes("__empty__");
        const hasManufacturerMatch = manufacturers.includes(rowManufacturer);

        // __empty__ 필터가 선택되었고 제조사가 비어있는 경우
        if (
          hasEmptyFilter &&
          (rowManufacturer === undefined ||
            rowManufacturer === null ||
            rowManufacturer === "")
        ) {
          return true;
        }

        // 일반 제조사 필터 매칭 (undefined가 아닌 경우만)
        if (
          hasManufacturerMatch &&
          rowManufacturer !== undefined &&
          rowManufacturer !== null &&
          rowManufacturer !== ""
        ) {
          return true;
        }

        // 둘 다 매칭되지 않으면 제외
        return false;
      }

      return true;
    },
    []
  );

  const columns = useMemo(
    () =>
      createChemicalSpecColumns({
        ownTypeFilter,
        setOwnTypeFilter: onChangeOwnTypeFilter,
        manufacturerFilter,
        setManufacturerFilter: onChangeManufacturerFilter,
        manufacturers,
        hasEmptyManufacturers,
      }),
    [
      ownTypeFilter,
      manufacturerFilter,
      manufacturers,
      hasEmptyManufacturers,
      onChangeOwnTypeFilter,
      onChangeManufacturerFilter,
    ]
  );

  const table = useReactTable({
    data: reagents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter: globalFilterValue,
    },
    globalFilterFn,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: Number.MAX_SAFE_INTEGER,
      },
    },
  });

  const onCancel = useCallback(() => {
    // 모든 라디오 버튼 선택 해제
    const resetValues = {
      reagents: reagents
        .filter((r) => r.alt_items.length > 0)
        .map((r) => ({
          spec_id: r.spec_id,
          alt_items: r.alt_items.map((item) => ({
            alt_map_id: item.alt_map_id,
            selected: false,
          })),
        })),
    };

    form.reset(resetValues);
  }, [form, reagents]);

  const onSubmit = useCallback(
    (data: z.infer<typeof reagentsSchema>) => {
      mutate(data, {
        onSuccess: () => {
          toast.success("성공적으로 저장되었습니다.", {
            position: "top-center",
            style: {
              width: "max-content",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "10px 16px",
            },
            classNames: {
              title: "font-semibold",
            },
          });

          // TODO: 여기서 쿼리 데이터를 이렇게 가져오는 방법이 어색함. 관심사 분리가 필요.
          const queryKey = QUERY_KEYS.SPEC.REAGENTS(projectId ?? "");
          const updatedData =
            queryClient.getQueryData<SpecReadReagentsResponse>(queryKey);
          const updatedInitialValues = convertToFormValues(updatedData);
          form.reset(updatedInitialValues);
        },
        onError: (error) => {
          console.error("error > ", error);
          toast.error("저장에 실패했습니다.", {
            position: "top-center",
            style: {
              width: "max-content",
              left: "50%",
              transform: "translateX(-50%)",
              padding: "10px 16px",
            },
            classNames: {
              title: "font-semibold",
            },
          });
        },
      });
    },
    [mutate, form, initialValues]
  );

  useEffect(() => {
    if (budget_amount) {
      setBudgetPrice(budget_amount);
    }
  }, [budget_amount]);

  useEffect(() => {
    if (reagents.length === 0) {
      return;
    }

    const totalConsumerPrice = reagents.reduce((acc, curr) => {
      const { item, alt_items, own_type, quantity } = curr;
      if (own_type === ReagentOwnType.IN_HOUSE) {
        return acc + parsePrice(item.list_price) * quantity;
      } else if (own_type === ReagentOwnType.ALTERNATIVE) {
        const selectedAltItems = alt_items.find(
          (alt_item) => alt_item.selected
        );
        if (selectedAltItems) {
          return acc + parsePrice(selectedAltItems.item.list_price) * quantity;
        }
      }
      return acc;
    }, 0);

    // 자사제품 합계 계산
    const flooredTotal = Math.floor(totalConsumerPrice) * 1.1; // VAT 포함
    setSumPrice(flooredTotal);

    // 자사제품 비중 계산
    if (budget_amount > 0) {
      const matchRate = Math.floor((flooredTotal / budget_amount) * 100);
      setMatchRate(`${matchRate}%`);
    } else {
      setMatchRate("-");
    }
  }, [reagents, budget_amount, setBudgetPrice, setSumPrice, setMatchRate]);

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={table.getAllColumns().length}>
            <Skeleton className="w-full h-[636px] rounded-xl" />
          </TableCell>
        </TableRow>
      );
    }

    if (table.getRowModel().rows.length > 0) {
      return table.getRowModel().rows.map((row, rowIndex) => (
        <TableRow
          key={row.id}
          className={cn(
            row.getIsSelected() && "bg-green/20",
            rowIndex % 2 === 0 ? "bg-white" : "bg-light-gray"
          )}
        >
          {row.getVisibleCells().map((cell, cellIndex, arr) => {
            const isFirst = cellIndex === 0;
            const isLastCell = cellIndex === arr.length - 1;

            return (
              <TableCell
                key={cell.id}
                style={{
                  minWidth: cell.column.columnDef.size,
                  maxWidth: cell.column.columnDef.size,
                }}
                className={cn(
                  "min-h-12 py-[10px] px-2",
                  rowIndex === table.getRowModel().rows.length - 1 &&
                    isFirst &&
                    "rounded-bl-md",
                  rowIndex === table.getRowModel().rows.length - 1 &&
                    isLastCell &&
                    "rounded-br-md"
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            );
          })}
        </TableRow>
      ));
    }

    return (
      <TableRow>
        <TableCell colSpan={columns.length} className="h-24 text-center">
          검색 결과가 없습니다.
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-5">
          <Card icon={BidAnalyticsIcon} title="시약/초자류 분석" noBorder>
            <div className="px-5 pb-5 relative">
              <Table>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableHeader key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          key={header.id}
                          colSpan={header.colSpan}
                          className="px-2 text-sm text-white bg-dark-red-60 first:rounded-tl-md last:rounded-tr-md"
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableHeader>
                ))}
                <TableBody>{renderTableBody()}</TableBody>
              </Table>

              <div
                className={cn("absolute -top-10 right-5 flex gap-2", {
                  hidden: !isDirty,
                })}
              >
                <Button
                  type="button"
                  variant="cancel"
                  size="trigger"
                  onClick={onCancel}
                >
                  취소하기
                </Button>
                <Button
                  type="submit"
                  variant="confirm"
                  size="trigger"
                  className="bg-green border-green hover:bg-green/80"
                >
                  저장하기
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </form>
    </Form>
  );
}
